import { useState } from 'react';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';

interface GateConfig {
  require_all_signatures: boolean;
  require_compliance_pass: boolean;
  human_review_on_reject: boolean;
}

interface GateConfigCardProps {
  clientId: string;
  config: GateConfig | null;
  isLoading: boolean;
  onConfigUpdated: (config: GateConfig) => void;
  className?: string;
}

const DEFAULT_CONFIG: GateConfig = {
  require_all_signatures: true,
  require_compliance_pass: true,
  human_review_on_reject: true,
};

export function GateConfigCard({
  clientId,
  config,
  isLoading,
  onConfigUpdated,
  className,
}: GateConfigCardProps) {
  const active = config ?? DEFAULT_CONFIG;
  const [draft, setDraft] = useState<GateConfig>(active);
  const [saving, setSaving] = useState(false);

  const toggle = (field: keyof GateConfig) =>
    { setDraft((prev) => ({ ...prev, [field]: !prev[field] })); };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('clients')
      .update({ gate_config: draft })
      .eq('id', clientId);
    setSaving(false);
    if (error) {
      toast.error('Failed to update gate configuration. Please try again.');
      setDraft(active); // revert
      return;
    }
    toast.success('Gate configuration updated.');
    onConfigUpdated(draft);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Default Gate Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* FR-077 mandatory notice */}
        <div
          className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"
          role="note"
          aria-label="Human review policy notice"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          <span>
            All contracts require human review before activation. There is no auto-approve option.
            These defaults apply to new contract reviews unless overridden at submission.
          </span>
        </div>

        {(
          [
            { field: 'require_all_signatures', label: 'Require All Signatures', id: 'gc-sigs' },
            { field: 'require_compliance_pass', label: 'Require Compliance Pass', id: 'gc-compliance' },
            { field: 'human_review_on_reject', label: 'Human Review on Reject', id: 'gc-review' },
          ] as const
        ).map(({ field, label, id }) => (
          <div key={field} className="flex items-center justify-between">
            <Label htmlFor={id} className="cursor-pointer text-sm">
              {label}
            </Label>
            <Switch
              id={id}
              checked={draft[field]}
              onCheckedChange={() => { toggle(field); }}
              aria-labelledby={id}
            />
          </div>
        ))}

        <Button onClick={() => { void handleSave(); }} disabled={saving} size="sm">
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </CardContent>
    </Card>
  );
}
